<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { caregiversApi } from '@/api/caregivers';
import { patientsApi } from '@/api/patients';
import { useAddressGeocoding, type GeocodeResult } from '@/composables/useAddressGeocoding';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// Caregivers store a single address + coordinates on the record; patients use the PatientAddress
// model (a flagged patient has none, so we create one primary address). Both go through the
// existing partial `update` endpoints.

type EntityType = 'caregiver' | 'patient';

const props = defineProps<{
  modelValue: boolean;
  entityType: EntityType;
  entityId: number;
  entityName: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const { t } = useI18n();
const queryClient = useQueryClient();
const { attachAutocomplete, openMapPicker } = useAddressGeocoding();

const uid = useId();
const INPUT_ID = `route-plan-address-input-${uid}`;
const MAP_ID = `route-plan-address-map-${uid}`;

const open = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const address = ref('');
const latitude = ref<string | null>(null);
const longitude = ref<string | null>(null);
const showMap = ref(false);
const saveError = ref<string | null>(null);

const hasCoordinates = computed(() => !!(latitude.value && longitude.value));
const canSave = computed(() => !!address.value && hasCoordinates.value);

// Callbacks receive only the fields that resolved, so we assign conditionally and never clobber
// an existing value with a blank (mirrors patient-addresses-form.vue).
function applyGeocode(result: GeocodeResult): void {
  if (result.address) address.value = result.address;
  if (result.latitude) latitude.value = result.latitude;
  if (result.longitude) longitude.value = result.longitude;
}

// Manual edits invalidate the geocoded coordinates, forcing the user to re-pick a geocoded result.
function onAddressInput(): void {
  latitude.value = null;
  longitude.value = null;
}

function resetForm(): void {
  address.value = '';
  latitude.value = null;
  longitude.value = null;
  showMap.value = false;
  saveError.value = null;
}

let detachAutocomplete: (() => void) | null = null;

watch(() => props.modelValue, async (isOpen) => {
  if (!isOpen) {
    detachAutocomplete?.();
    detachAutocomplete = null;

    return;
  }
  resetForm();
  await nextTick();
  detachAutocomplete = await attachAutocomplete(INPUT_ID, applyGeocode);
});

onUnmounted(() => detachAutocomplete?.());

async function openMap(): Promise<void> {
  if (!hasCoordinates.value) return;
  showMap.value = true;
  // The map div lives inline in the already-mounted dialog, so one tick is enough for v-if to
  // render it before we hand the element to Google Maps.
  await nextTick();
  const position = { lat: parseFloat(latitude.value as string), lng: parseFloat(longitude.value as string) };
  await openMapPicker(MAP_ID, position, applyGeocode);
}

const { mutate: save, isPending } = useMutation({
  mutationFn: async (): Promise<void> => {
    if (props.entityType === 'caregiver') {
      await caregiversApi.update({
        id: props.entityId,
        address: address.value,
        latitude: latitude.value ?? undefined,
        longitude: longitude.value ?? undefined,
      });

      return;
    }

    // Patients reach this dialog only when flagged (no usable geocoded address), so creating a
    // single primary address is correct and won't demote an existing one.
    await patientsApi.update({
      id: props.entityId,
      patientAddressesAttributes: [{
        address: address.value,
        latitude: latitude.value,
        longitude: longitude.value,
        primary: true,
        locationExtended: false,
      }],
    });
  },
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: props.entityType === 'caregiver' ? ['caregivers'] : ['patientsActive'],
    });
    open.value = false;
  },
  onError: () => {
    saveError.value = t('routePlan.addressSaveError');
  },
});

function onSubmit(): void {
  if (!canSave.value) return;
  saveError.value = null;
  save();
}
</script>

<template>
  <!-- persistent: the Places dropdown is appended to <body>, outside the overlay content, so
       clicking a suggestion would otherwise count as an outside click and close the dialog
       mid-selection. Closing stays available through the toolbar X and the cancel button. -->
  <responsive-dialog
    v-model="open"
    :title="t('routePlan.addressDialogTitle', { name: entityName })"
    :max-width="560"
    scrollable
    persistent
    :retain-focus="false"
  >
    <template #default>
      <v-text-field
        :id="INPUT_ID"
        v-model="address"
        variant="outlined"
        prepend-inner-icon="mdi-magnify"
        autocomplete="off"
        :label="t('routePlan.visitAddress')"
        :placeholder="t('patientForm.addressSearchPlaceholder')"
        hide-details="auto"
        @input="onAddressInput"
      >
        <template #append-inner>
          <v-tooltip
            location="top"
            :text="t('patientForm.addressMap')"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                icon="mdi-map-search-outline"
                variant="text"
                size="small"
                :disabled="!hasCoordinates"
                @click="openMap"
              />
            </template>
          </v-tooltip>
        </template>
      </v-text-field>

      <v-expand-transition>
        <div
          v-if="showMap"
          class="mt-3"
        >
          <div
            :id="MAP_ID"
            class="address-map"
          />
          <div class="d-flex justify-end mt-2">
            <v-btn
              variant="text"
              size="small"
              @click="showMap = false"
            >
              {{ t('routePlan.confirm') }}
            </v-btn>
          </div>
        </div>
      </v-expand-transition>

      <v-alert
        v-if="address && !hasCoordinates"
        type="info"
        variant="tonal"
        density="compact"
        class="mt-3"
      >
        {{ t('routePlan.addressRequiresLocation') }}
      </v-alert>

      <v-alert
        v-if="saveError"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-3"
      >
        {{ saveError }}
      </v-alert>
    </template>
    <template #actions="{ close }">
      <v-btn
        variant="text"
        @click="close"
      >
        {{ t('routePlan.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!canSave"
        :loading="isPending"
        @click="onSubmit"
      >
        {{ t('routePlan.saveAddress') }}
      </v-btn>
    </template>
  </responsive-dialog>
</template>

<style scoped>
.address-map {
  width: 100%;
  height: 320px;
}
</style>
